/**
 * S2S JWT verifier.
 *
 * Lifted 1:1 from provisioning's `server/lib/auth/s2s-verify.ts` with one
 * change: the JWKS source is configured via `setJwksResolver(...)` rather
 * than baked in at module load. Provisioning hardcoded
 * `region: "ap-south-1", bucket: "nucleus-cloud-s2s-auth"`; that moves to
 * the consumer's bootstrap so the package is portable.
 *
 * Verifier semantics (algorithms, clock skew, claim shape, scope parsing)
 * are unchanged. Failure modes surface as typed `S2STokenExpired` /
 * `S2STokenInvalid` / `S2SIssuerMismatch` / `S2SAudienceMismatch` so
 * downstream consumers can `instanceof`-discriminate (parity with the
 * Python + Go ports, which expose typed errors).
 */
import { errors as joseErrors, jwtVerify } from "jose";
import type { JWTPayload } from "jose";
import { JtiReplayed } from "../types/jti-replayed";
import type { ReplayStore } from "../types/replay-store";
import {
  S2SAudienceMismatch,
  S2SIssuerMismatch,
  S2SReplayStoreUnavailable,
  S2STokenExpired,
  S2STokenInvalid,
  S2SVerifyError,
} from "./errors";
import { processGlobalSlot } from "../global-state";
import type { GetKeyFn } from "./sigv4-s3-jwks";

// ⚠️ `JtiReplayed` IS RE-EXPORTED HERE, and it is not cosmetic. This module
// THROWS it, and tsup builds each `exports` subpath as its own bundle with
// `splitting: false` — so `@nodii/grpc-auth/types` ships a SEPARATE COPY of the
// class. A consumer that imported it from `/types` and caught an error thrown
// by `/verify` got `instanceof === false`, silently, and fell through to its
// generic arm. Measured against dist/: an instance built from
// `dist/types/index.js` is NOT `instanceof dist/verify/index.js`'s
// `S2SVerifyError`. A bundle must export every error class it throws so a
// caller can obtain the SAME class object it will be catching.
export {
  JtiReplayed,
  S2SAudienceMismatch,
  S2SIssuerMismatch,
  S2SReplayStoreUnavailable,
  S2STokenExpired,
  S2STokenInvalid,
  S2SVerifyError,
};

// The resolver lives on a process-global slot (NOT a module-level `let`) so
// that the tsup `splitting: false` subpath bundles — each of which inlines its
// own copy of this module — all read/write the SAME resolver. Otherwise a
// service that calls the root `setJwksResolver` but verifies via
// `@nodii/grpc-auth/server` would hit a different, never-set copy and throw
// "JWKS resolver not configured" on every token (req 4f1facd4).
const jwksSlot = processGlobalSlot<GetKeyFn>("jwksResolver");

/**
 * Wire the JWKS source. Call once at boot, before the first
 * `verifyS2SToken` invocation. Typically this is `createSigV4S3JWKSet(...)`.
 *
 * The resolver is stored process-globally, so it is shared across ALL
 * `@nodii/grpc-auth` subpath entrypoints (root / `/server` / `/verify`) —
 * the import path can no longer silently split it.
 */
export function setJwksResolver(resolver: GetKeyFn): void {
  jwksSlot.set(resolver);
}

/**
 * Test-only — clears the process-global JWKS resolver. Because the resolver is
 * now process-global (so subpath bundles share it), a test that wires a
 * resolver would otherwise leak it into sibling tests; this hook (also invoked
 * by `resetGrpcAuthConfigForTesting`) restores the unset state. NOT a
 * production code path.
 */
export function resetJwksResolverForTesting(): void {
  jwksSlot.set(null);
}

export type VerifiedS2SToken = {
  issuer: string;
  subject: string;
  /**
   * RFC 7519 § 4.1.3 — `aud` is either a single string or an array of
   * strings. Per polyglot parity (drift `0707f386`), the TS surface
   * normalizes to ALWAYS `string[]` to match the Go SDK's `[]string`
   * shape (and the Python SDK's `list[str]`). A scalar `aud` claim is
   * lifted to a 1-element array during verify. Callers that need the
   * raw JWT shape can read `raw.aud`.
   */
  audience: string[];
  scopes: string[];
  serviceId: string;
  serviceName: string;
  instanceId?: string;
  issuedAt: number;
  expiresAt: number;
  /**
   * `jti` claim (RFC 7519 § 4.1.7) — JWT id, unique per-issued-token.
   * Always populated when the verifier sees a string `jti` claim; the
   * claim is REQUIRED whenever `replayStore` is set in `VerifyOptions`
   * (per `01-communication-doctrine § 6` + auth-004 acceptance gate).
   */
  jti?: string;
  raw: JWTPayload;
};

export type VerifyOptions = {
  expectedAudience: string;
  expectedIssuer: string;
  maxClockSkewSeconds?: number;
  /**
   * Optional jti-replay store. When provided, the verifier:
   *   1. requires a non-empty string `jti` claim (otherwise throws
   *      `S2STokenInvalid("missing_jti")`)
   *   2. calls `replayStore.reserve(jti, ttlMs)` AFTER all other claim
   *      checks pass
   *   3. throws `JtiReplayed` if `reserve` returns `false`
   *
   * Per R1 the lib ships NO in-memory default. `configureGrpcAuth({...
   * replayStore: ...})` is the canonical wiring point; passing a store
   * directly to `verifyS2SToken` is supported for tests + advanced
   * single-call overrides.
   */
  replayStore?: ReplayStore;
  /**
   * TTL (seconds) for the replay-store entry. When `undefined`, the
   * verifier computes `expiresAt - now()` and uses that (clamped to a
   * minimum of 1s) — so the replay entry self-expires the moment the
   * token does.
   */
  replayTtlSeconds?: number;
  /**
   * Clock injection (epoch seconds). Defaults to `Math.floor(Date.now()/1000)`.
   * Used by replay-TTL computation; parity with the Python + Go ports.
   */
  nowFn?: () => number;
};

function requireStringClaim(payload: JWTPayload, key: string): string {
  const value = payload[key];
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`Missing or invalid '${key}' claim`);
  }
  return value;
}

function optionalStringClaim(
  payload: JWTPayload,
  key: string,
): string | undefined {
  const value = payload[key];
  return typeof value === "string" ? value : undefined;
}

export async function verifyS2SToken(
  token: string,
  opts: VerifyOptions,
): Promise<VerifiedS2SToken> {
  const jwks = jwksSlot.get();
  if (!jwks) {
    throw new S2STokenInvalid(
      "JWKS resolver not configured. Call setJwksResolver(...) before verifyS2SToken().",
    );
  }

  let payload: JWTPayload;
  let protectedHeader: { kid?: string; alg?: string };
  try {
    const out = await jwtVerify(token, jwks, {
      issuer: opts.expectedIssuer,
      audience: opts.expectedAudience,
      algorithms: ["RS256"],
      clockTolerance: opts.maxClockSkewSeconds ?? 10,
    });
    payload = out.payload;
    protectedHeader = out.protectedHeader;
  } catch (err: unknown) {
    // Translate jose's typed errors into our verifier-typed errors so
    // downstream consumers can `instanceof`-discriminate without taking
    // a direct dependency on jose's error hierarchy.
    if (err instanceof joseErrors.JWTExpired) {
      throw new S2STokenExpired("token_expired", err);
    }
    if (err instanceof joseErrors.JWTClaimValidationFailed) {
      if (err.claim === "iss") {
        throw new S2SIssuerMismatch("issuer_mismatch", err);
      }
      if (err.claim === "aud") {
        throw new S2SAudienceMismatch("audience_mismatch", err);
      }
      throw new S2STokenInvalid(`claim_invalid:${err.claim}`, err);
    }
    if (err instanceof joseErrors.JOSEAlgNotAllowed) {
      // `invalid_algorithm` is the canonical wire-stable code (auth-sdk
      // middleware lens). Jose's own `code` is intentionally NOT
      // propagated into the public message so consumers don't bind to
      // jose's enum — they instanceof-discriminate via `S2STokenInvalid`
      // and the jose error stays attached on `.cause` for debug.
      throw new S2STokenInvalid("invalid_algorithm", err);
    }
    if (
      err instanceof joseErrors.JWSSignatureVerificationFailed ||
      err instanceof joseErrors.JWSInvalid ||
      err instanceof joseErrors.JWTInvalid ||
      err instanceof joseErrors.JWKSNoMatchingKey ||
      err instanceof joseErrors.JWKSMultipleMatchingKeys ||
      err instanceof joseErrors.JWKSInvalid
    ) {
      throw new S2STokenInvalid("token_invalid", err);
    }
    // Catch-all: opaque sentinel rather than jose's `err.code` so the
    // public message surface stays owned by this package.
    if (err instanceof joseErrors.JOSEError) {
      throw new S2STokenInvalid("token_invalid", err);
    }
    throw new S2STokenInvalid("token_invalid", err);
  }

  if (!protectedHeader.kid) {
    throw new S2STokenInvalid("Missing kid in token header");
  }

  // Defence-in-depth: jose's `algorithms` option already filters, but a
  // future contributor swapping the verifier could regress; pin here too.
  // Message uses the canonical `invalid_algorithm` sentinel (auth-sdk
  // middleware lens) — wire-stable across the trust boundary.
  if (protectedHeader.alg !== "RS256") {
    throw new S2STokenInvalid("invalid_algorithm");
  }

  // An explicit empty expectation is invalid configuration, never an opt-out.
  // jose treats empty strings as unset; check after verification so malformed
  // and bad-signature inputs retain their token_invalid classification.
  if (opts.expectedIssuer === "")
    throw new S2SIssuerMismatch("issuer_mismatch");
  if (opts.expectedAudience === "")
    throw new S2SAudienceMismatch("audience_mismatch");

  if (!payload.sub) {
    throw new S2STokenInvalid("Missing sub claim");
  }
  const sub = payload.sub;

  if (!payload.exp || !payload.iat) {
    throw new S2STokenInvalid("Missing exp/iat claims");
  }
  const { exp, iat } = payload;

  if (!payload.iss) {
    throw new S2STokenInvalid("Missing iss claim");
  }
  const iss = payload.iss;

  const scopes = Array.isArray(payload.scope)
    ? payload.scope.filter((s): s is string => typeof s === "string")
    : typeof payload.scope === "string"
      ? payload.scope.split(" ")
      : [];

  let serviceName: string;
  try {
    serviceName = requireStringClaim(payload, "svc");
  } catch (err) {
    throw new S2STokenInvalid(
      err instanceof Error ? err.message : "Missing svc claim",
      err,
    );
  }
  const serviceId = optionalStringClaim(payload, "sid") || sub;
  const instanceId = optionalStringClaim(payload, "instance_id");

  // Normalize `aud` to `string[]` per drift `0707f386` (polyglot parity
  // with Go's `[]string` + Python's `list[str]`). RFC 7519 § 4.1.3
  // allows either shape on the wire; callers needing the raw shape
  // can read `raw.aud`.
  const rawAud = payload.aud;
  const audience: string[] =
    rawAud === undefined ? [] : Array.isArray(rawAud) ? rawAud : [rawAud];

  const jtiClaim = payload.jti;
  const jti =
    typeof jtiClaim === "string" && jtiClaim.length > 0 ? jtiClaim : undefined;

  // jti-replay check — DEPRECATED opt-in. Hub decision D845: callers cache and
  // reuse one token for its lifetime, so single-use is OFF by default and this
  // branch runs only when a caller explicitly passes a `replayStore`. Runs AFTER
  // all other claim validations so a bad-signature token never touches the
  // store. The `jti` requirement is UNCHANGED by D845 (owner ruling 2026-09-18):
  // a token with no jti is rejected only under this opt-in, exactly as before.
  if (opts.replayStore) {
    if (!jti) {
      throw new S2STokenInvalid("missing_jti");
    }
    const ttlSeconds =
      opts.replayTtlSeconds ??
      Math.max(
        1,
        exp - (opts.nowFn ? opts.nowFn() : Math.floor(Date.now() / 1000)),
      );
    // TYPED, not a bare `await`. A raw driver error escaping here landed in
    // every caller's default arm and was reported as `token_invalid` — a Redis
    // blip made indistinguishable from a forged token. `gateway-hmac/bound.ts`
    // in this same package already wrapped its equivalent call and returned a
    // typed REPLAY_STORE_ERROR; the S2S verifier was the outlier. FAIL-CLOSED:
    // an unreachable store means we cannot prove this jti is unseen, so the
    // token does not pass — but the caller is told WHY, and "retry" is a
    // different answer from "re-mint".
    let ok: boolean;
    try {
      ok = await opts.replayStore.reserve(jti, ttlSeconds * 1000);
    } catch (err) {
      throw new S2SReplayStoreUnavailable("replay_store_unavailable", err);
    }
    if (!ok) {
      throw new JtiReplayed(jti);
    }
  }

  return {
    issuer: iss,
    subject: sub,
    audience,
    scopes,
    serviceId,
    serviceName,
    instanceId,
    issuedAt: iat,
    expiresAt: exp,
    jti,
    raw: payload,
  };
}
